const OSS_SIGNATURE_QUERY_KEYS = new Set([
  'ossaccesskeyid',
  'signature',
  'x-oss-signature',
  'auth_key',
])

/**
 * @description 判断 URL 是否已经携带 OSS V1/V4 或 CDN 下载签名。
 * @param {string} value - 待检测 URL
 * @returns {boolean}
 */
export function isOssSignedUrl(value) {
  if (!value || typeof value !== 'string') return false

  try {
    const parsed = new URL(value.startsWith('//') ? `https:${value}` : value)
    return Array.from(parsed.searchParams.keys()).some((key) =>
      OSS_SIGNATURE_QUERY_KEYS.has(key.toLowerCase())
    )
  } catch {
    return false
  }
}

const parseOssV4Date = (value) => {
  const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(value || '')
  if (!match) return Number.NaN
  return Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
    Number(match[6])
  )
}

/** @description Resolve OSS V1/V4 signed URL expiry as epoch milliseconds. */
export function getOssSignedUrlExpiresAt(value) {
  if (!isOssSignedUrl(value)) return null
  try {
    const parsed = new URL(value.startsWith('//') ? `https:${value}` : value)
    // CDN auth_key 的时间戳是签发时间，有效期由 CDN 配置决定，不能在客户端推算。
    if (Array.from(parsed.searchParams.keys()).some((key) => key.toLowerCase() === 'auth_key')) {
      return null
    }
    const v1Expires = Number(parsed.searchParams.get('Expires'))
    if (Number.isFinite(v1Expires) && v1Expires > 0) return v1Expires * 1000

    const signedAt = parseOssV4Date(parsed.searchParams.get('x-oss-date'))
    const expiresInSeconds = Number(parsed.searchParams.get('x-oss-expires'))
    return Number.isFinite(signedAt) && Number.isFinite(expiresInSeconds) && expiresInSeconds >= 0
      ? signedAt + expiresInSeconds * 1000
      : null
  } catch {
    return null
  }
}

export function isOssSignedUrlExpiring(value, skewMs = 30_000, nowMs = Date.now()) {
  const expiresAt = getOssSignedUrlExpiresAt(value)
  return expiresAt !== null && expiresAt <= nowMs + Math.max(0, Number(skewMs) || 0)
}

/**
 * @description 用 host+pathname 识别同一 OSS 对象，忽略签名查询参数。
 * @param {string} value - 已签名或待比较的 URL
 * @returns {string|null}
 */
export function getOssSignedUrlStableIdentity(value) {
  if (!isOssSignedUrl(value)) return null

  try {
    const parsed = new URL(value.startsWith('//') ? `https:${value}` : value)
    return `${parsed.host.toLowerCase()}${parsed.pathname}`
  } catch {
    return null
  }
}

const SIGNED_URL_IN_TEXT = /https?:\/\/[^\s"'<>\\]+/gi

function pushUniqueSignedUrl(acc, seen, value) {
  if (!isOssSignedUrl(value) || seen.has(value)) return
  seen.add(value)
  acc.push(value)
}

/**
 * @description 从字符串、HTML 或配置对象中收集已签名 OSS URL。
 * @param {unknown} value - 响应、卡片或 HTML
 * @param {string[]} [acc]
 * @returns {string[]}
 */
export function collectOssSignedUrls(value, acc = [], seen = new Set(), visited = new WeakSet()) {
  if (typeof value === 'string') {
    if (isOssSignedUrl(value)) {
      pushUniqueSignedUrl(acc, seen, value)
      return acc
    }

    const matches = value.match(SIGNED_URL_IN_TEXT) || []
    for (const match of matches) {
      pushUniqueSignedUrl(acc, seen, match.replace(/[),.;]+$/, ''))
    }
    return acc
  }

  if (!value || typeof value !== 'object') return acc
  if (visited.has(value)) return acc
  visited.add(value)

  if (Array.isArray(value)) {
    for (const item of value) collectOssSignedUrls(item, acc, seen, visited)
    return acc
  }

  for (const item of Object.values(value)) {
    collectOssSignedUrls(item, acc, seen, visited)
  }
  return acc
}

/**
 * @description 在刷新后的载荷中找到同一对象的新签名 URL。
 * @param {string} previousUrl - 页面上已过期或即将过期的签名 URL
 * @param {unknown} nextPayload - 重新拉取的响应
 * @returns {string|null}
 */
export function findRefreshedOssSignedUrl(previousUrl, nextPayload) {
  const identity = getOssSignedUrlStableIdentity(previousUrl)
  if (!identity) return null

  return (
    collectOssSignedUrls(nextPayload).find(
      (url) => getOssSignedUrlStableIdentity(url) === identity
    ) || null
  )
}

/**
 * @description 打开方案页签名文件前用最新载荷替换过期签名；站内路由和未签名 URL 原样返回。
 * @param {string} previousUrl - 卡片或正文里现有的访问 URL
 * @param {() => Promise<unknown>} loadContent - 重新拉取方案配置
 * @returns {Promise<string>}
 */
export async function resolveSolutionHomeSignedFileUrl(previousUrl, loadContent) {
  if (!previousUrl) return ''
  if (!isOssSignedUrl(previousUrl)) return previousUrl
  if (typeof loadContent !== 'function') {
    throw new Error('方案文件刷新函数缺失')
  }

  const nextUrl = findRefreshedOssSignedUrl(previousUrl, await loadContent())
  if (!nextUrl) {
    throw new Error('方案文件签名已过期，请刷新页面后重试')
  }
  return nextUrl
}
